/**
 * Contact page, the console — the form on paper; on the night side, the
 * request it is about to send, printed live, then what came back. The form
 * is the server's (templates/site/sections/contact/console.html.twig); the
 * words the console answers with come in the page's data ("contact/console").
 *
 * @module sections/contact/console
 */

import { REDUCED_MOTION } from '../../config.js';
import { $, $$ } from '../../lib/dom.js';
import { ScrollTrigger, gsap } from '../../lib/gsap.js';
import { renderToString } from '../../lib/html.js';
import { pageEntry } from '../../lib/page-data.js';
import { getSmoother } from '../../lib/scroll.js';
import { terminalLines } from '../../components/devices.js';

/**
 * The message box grows with what is written (no scrollbar inside it, up to
 * about half the screen); while you write at its end, the page scrolls so
 * the line being typed stays in view.
 *
 * @param {HTMLTextAreaElement} box
 */
function growMessageBox(box) {
    let base = 0;
    let refresh = 0;

    const fit = () => {
        const before = box.offsetHeight;
        base ||= before;

        box.style.height = 'auto';
        const max = Math.round(window.innerHeight * 0.55);
        const wanted = box.scrollHeight + (box.offsetHeight - box.clientHeight);
        box.style.height = `${Math.min(max, Math.max(base, wanted))}px`;
        box.style.overflowY = wanted > max ? 'auto' : 'hidden';

        // The page got taller: let the smoother and the triggers know.
        if (box.offsetHeight !== before) {
            window.clearTimeout(refresh);
            refresh = window.setTimeout(() => ScrollTrigger.refresh(), 250);
        }
    };

    const follow = () => {
        if (document.activeElement !== box || box.selectionEnd < box.value.length - 1) {
            return;
        }

        const below = box.getBoundingClientRect().bottom - window.innerHeight * 0.82;

        if (below <= 0) {
            return;
        }

        const smoother = getSmoother();

        if (smoother) {
            smoother.scrollTo(smoother.scrollTop() + below, true);
        } else {
            window.scrollBy({ top: below, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
        }
    };

    box.addEventListener('input', () => {
        fit();
        follow();
    });
    box.form.addEventListener('reset', () => window.setTimeout(fit, 0));
    window.addEventListener('resize', fit);
    fit();
}

/**
 * Easter egg: the terminal answers a few commands typed in the message,
 * the way a shell would — a sandwich for whoever says sudo (xkcd 149).
 *
 * @type {Array<[RegExp, import('../../components/devices.js').TerminalLine]>}
 */
const SHELL_JOKES = [
    [/\bsudo make me a sandwich\b/i, ['hi', '> Okay.']],
    [/\bmake me a sandwich\b/i, ['err', '! What? Make it yourself.']],
    [/\brm -rf \/(?!\S)/, ['err', '! rm: refusing to remove the whole hood. Nice try.']],
    [/\bgit blame\b/i, ['note', '> It was the intern. There is no intern.']],
    [/\b(?:hire|hired)\b.*\bjul6art\b|\bjul6art\b.*\bhire/i, ['hi', '> Wise choice. Press Send.']],
];

/**
 * The night side prints the request the form is about to send — the fields
 * the contact route reads, the mission when one is picked — and then what
 * came back.
 *
 * @param {HTMLFormElement} form
 */
function printRequest(form) {
    const body = $('[data-live-terminal] [data-terminal-body]');

    if (!body) {
        return;
    }

    // The briefing, without its caret.
    const briefing = body.innerHTML.replace(/\n?<span class="terminal__caret"[^>]*><\/span>\s*$/, '');
    let state = '';
    let status = [];

    // A quoted shell argument, cut to length.
    const argument = (value, max) => {
        let flat = String(value ?? '')
            .replace(/\s+/g, ' ')
            .trim();

        if (flat.length > max) {
            flat = `${flat.slice(0, max - 1)}…`;
        }

        return `"${flat.replaceAll('\\', '\\\\').replaceAll('"', '\\"')}"`;
    };

    const draw = () => {
        const data = new FormData(form);
        const message = String(data.get('message') ?? '');
        const mission = String(data.get('mission') ?? '');
        const joke = SHELL_JOKES.find(([command]) => command.test(message));

        const lines = [
            ['out', ''],
            ['cmd', `$ curl -X POST ${form.getAttribute('action')} \\`],
            ['out', `    -F name=${argument(data.get('name'), 30)} \\`],
            ['out', `    -F email=${argument(data.get('email'), 30)} \\`],
            ...(mission ? [['out', `    -F mission=${argument(mission, 30)} \\`]] : []),
            ['out', `    -F message=${argument(message, 44)}`],
            ...(joke ? [joke[1]] : []),
            ...status,
        ];

        body.innerHTML = `${briefing}\n${renderToString(terminalLines(lines))}`;
    };

    form.addEventListener('input', () => {
        if (state && state !== 'sending') {
            state = '';
            status = [];
        }
        draw();
    });

    form.addEventListener('reset', () => window.setTimeout(draw, 0));

    form.addEventListener('form:state', ({ detail }) => {
        state = detail.state;

        const lines = {
            sending: [['note', '> sending…']],
            ok: [
                ['hi', '< 200 OK'],
                ['out', detail.text],
            ],
            invalid: [['err', '! name, email and message are required']],
        };

        status = lines[state] ?? [
            ['err', '< request failed'],
            ['out', detail.text],
        ];

        draw();
    });

    draw();
}

/**
 * Sends the form without leaving the page, and tells the terminal how it
 * went (a `form:state` event).
 *
 * @param {HTMLFormElement} form
 */
function sendForm(form) {
    const note = $('[data-note]', form);
    const { success, error } = pageEntry('contact/console');

    /**
     * @param {string} text
     * @param {'' | 'is-ok' | 'is-error'} [modifier]
     */
    const say = (text, modifier = '') => {
        note.className = `form__note text-mono ${modifier}`.trim();
        note.textContent = text;
        gsap.fromTo(note, { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.4 });
    };

    /**
     * @param {'invalid' | 'sending' | 'ok' | 'error'} state
     * @param {string} [text]
     */
    const emit = (state, text = '') => form.dispatchEvent(new CustomEvent('form:state', { detail: { state, text } }));

    // The red line under a field draws itself on focus.
    $$('[data-field]', form).forEach((item) => {
        const input = $('input, textarea', item);
        const line = $('.field__line', item);

        input.addEventListener('focus', () => gsap.to(line, { scaleX: 1, duration: 0.6, ease: 'power3.out' }));
        input.addEventListener('blur', () => {
            if (!input.value) {
                gsap.to(line, { scaleX: 0, duration: 0.4 });
            }
        });
    });

    form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const button = $('button[type="submit"]', form);
        const data = new FormData(form);

        if (!data.get('name') || !data.get('email') || !data.get('message')) {
            say(error, 'is-error');
            emit('invalid', error);
            gsap.fromTo(form, { x: -6 }, { x: 0, duration: 0.5, ease: 'elastic.out(1,0.35)' });
            return;
        }

        say('Sending…');
        emit('sending');
        button.disabled = true;

        try {
            const response = await fetch(form.action, {
                method: 'POST',
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: data,
            });
            const result = await response.json().catch(() => null);

            if (!response.ok || result?.status !== true) {
                throw new Error(result?.message ?? `HTTP ${response.status}`);
            }

            say(success, 'is-ok');
            emit('ok', success);
            form.reset();
            gsap.fromTo($$('.field', form), { opacity: 0.4 }, { opacity: 1, duration: 0.6, stagger: 0.05 });
        } catch {
            say(error, 'is-error');
            emit('error', error);
        } finally {
            button.disabled = false;
        }
    });
}

export function animate() {
    const form = $('[data-form]');

    if (!form) {
        return;
    }

    printRequest(form);
    growMessageBox(form.elements.namedItem('message'));
    sendForm(form);
}
